import { useState } from "react";
import {
  CHANGE_IT_BACK,
  RENAME_YES,
  UNDO_REPLACE,
  bookerRenameTitle,
  bookerRenameWords,
  candidateLabel,
  notComingAskManager,
  notComingBlocked,
  notComingButton,
  notComingChooseCount,
  notComingHelp,
  notComingNeedsManager,
  notComingTitle,
  renameNo,
  renameQuestion,
  renameWhy,
  replaceThis,
  replacingNote,
  type GuestNoun,
  type NotComing,
} from "@vertu/shared";

/**
 * "Is this still {name}?" on a saved rider's card (P4a; words and rules in
 * @vertu/shared's rider-rename.ts). The booker's Yes is not theirs to give:
 * it turns into "change it back, or replace this rider", under a heading
 * that says so (the question they answered does not stay as the heading).
 * The heading takes the focus when a save stops to ask, and again once the
 * booker has answered Yes (the card's id prefix + "-rename"). `marked`
 * false: the list does not mark the name (every plain answer counts), so
 * the words speak of "details".
 */
export function RenamePrompt({
  idPrefix,
  who,
  name,
  noun,
  stage,
  marked = true,
  onAnswer,
}: {
  idPrefix: string;
  /** "Rider 2". */
  who: string;
  /** The saved rider's name ("" when none is readable). */
  name: string;
  noun: GuestNoun;
  stage: "ask" | "booker";
  marked?: boolean;
  onAnswer: (answer: "booker" | "back" | "replace") => void;
}) {
  const titleId = `${idPrefix}-rename`;
  return (
    <div className="vt-q-rename" role="group" aria-labelledby={titleId} data-stage={stage}>
      <p id={titleId} className="vt-q-rename-title" tabIndex={-1}>
        {stage === "ask" ? renameQuestion(name, noun) : bookerRenameTitle(noun, marked)}
      </p>
      {stage === "ask" ? (
        <>
          <p className="vt-q-rename-why">{renameWhy(who, marked)}</p>
          <div className="vt-q-rename-actions">
            <button type="button" className="vt-q-rename-btn" onClick={() => onAnswer("booker")}>
              {RENAME_YES}
            </button>
            <button type="button" className="vt-q-rename-btn" onClick={() => onAnswer("replace")}>
              {renameNo(noun)}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="vt-q-rename-why" role="status">
            {bookerRenameWords(name, noun, marked)}
          </p>
          <div className="vt-q-rename-actions">
            <button type="button" className="vt-q-rename-btn" onClick={() => onAnswer("back")}>
              {CHANGE_IT_BACK}
            </button>
            <button type="button" className="vt-q-rename-btn" onClick={() => onAnswer("replace")}>
              {replaceThis(noun)}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * On a replaced card until it is saved: whose place it takes, and Undo. It
 * takes the focus once Replace is chosen (`id`: the card's prefix +
 * "-replacing"), so a keyboard or screen-reader user hears what happened
 * and is at the card being filled in.
 */
export function ReplacingNote({
  id,
  name,
  noun,
  who,
  onUndo,
}: {
  id?: string;
  name: string;
  noun: GuestNoun;
  who: string;
  onUndo: () => void;
}) {
  return (
    <p className="vt-q-replacing" id={id} tabIndex={id ? -1 : undefined}>
      <span>{replacingNote(name, noun)}</span>
      <button type="button" className="vt-q-replacing-undo" aria-label={`Undo: keep ${name || who}`} onClick={onUndo}>
        {UNDO_REPLACE}
      </button>
    </p>
  );
}

/**
 * "Who is not coming?": more riders are saved than the booking is now for.
 * The guest ticks exactly the number to remove; each goes by id, answers and
 * all (nothing moves to anyone else). With nobody to choose (only empty
 * places in the way), one button clears them.
 */
export function NotComingChooser({
  idPrefix,
  noun,
  plan,
  busy,
  error,
  onRemove,
}: {
  idPrefix: string;
  noun: GuestNoun;
  plan: NotComing;
  busy: boolean;
  error: string | null;
  onRemove: (ids: string[]) => void;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const titleId = `${idPrefix}-notcoming`;
  const count = plan.needed > 0 ? notComingChooseCount(plan, chosen.length, noun) : null;
  const ready = chosen.length === plan.needed;
  // Staff below manager with too few riders theirs to remove: ask a manager
  // (never "Choose" with nothing to choose).
  const blocked = notComingBlocked(plan);
  if (blocked)
    return (
      <fieldset className="vt-q-notcoming" aria-labelledby={titleId} aria-describedby={`${titleId}-help`}>
        <legend id={titleId} className="vt-q-notcoming-title" tabIndex={-1}>
          {notComingTitle()}
        </legend>
        <p id={`${titleId}-help`} className="vt-q-notcoming-help">
          {notComingAskManager(plan, noun)}
        </p>
        <ul className="vt-q-notcoming-list">
          {plan.candidates.map((c) => (
            <li key={c.id}>
              {candidateLabel(c, noun)}
              {c.needsManager ? ` · ${notComingNeedsManager()}` : ""}
            </li>
          ))}
        </ul>
      </fieldset>
    );
  return (
    <fieldset className="vt-q-notcoming" aria-labelledby={titleId} aria-describedby={`${titleId}-help`}>
      <legend id={titleId} className="vt-q-notcoming-title" tabIndex={-1}>
        {notComingTitle()}
      </legend>
      <p id={`${titleId}-help`} className="vt-q-notcoming-help">
        {plan.needed > 0
          ? notComingHelp(plan, noun)
          : `This booking is now for ${plan.party} ${plan.party === 1 ? noun.one : noun.other}. The empty places in the way are cleared; everyone saved stays.`}
      </p>
      {plan.candidates.length > 0 &&
        plan.needed > 0 &&
        plan.candidates.map((c) => {
          const id = `${titleId}-${c.id}`;
          return (
            <label key={c.id} className="vt-q-choice" htmlFor={id}>
              <input
                id={id}
                type="checkbox"
                checked={chosen.includes(c.id)}
                disabled={busy || c.needsManager}
                onChange={(event) =>
                  setChosen((cur) => (event.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id)))
                }
              />
              <span>
                {candidateLabel(c, noun)}
                {c.needsManager ? ` · ${notComingNeedsManager()}` : ""}
              </span>
            </label>
          );
        })}
      {count && <p className="vt-q-notcoming-count">{count}</p>}
      {error && (
        <p className="vt-q-rider-message" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="vt-q-rename-btn vt-q-rename-btn-primary"
        aria-disabled={!ready || busy ? true : undefined}
        onClick={() => {
          if (!ready || busy) return;
          onRemove(chosen);
        }}
      >
        {busy ? "Removing…" : plan.needed > 0 ? notComingButton(plan, noun) : "Clear the empty places"}
      </button>
    </fieldset>
  );
}
